/********************************************* ASSETS *********************************************/
const s = {
    ...require('./submit-button.scss'),
    ...require('../input-util-components.scss'),
};

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import LaddaButton, {EXPAND_LEFT, XL} from 'react-ladda';
import {isStringOrNumber, FormSubmitHandler} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {onSubmitKeyPress, translate} from '@canimmunize/core-imports';

/*************************************** TYPE DEFINITIONS *****************************************/
export interface SubmitBtnProps {
    isLoading?: boolean;
    onClick?: FormSubmitHandler;
    // TODO Replace labelId with a direct translation
    labelId?: string;
    children?: string | JSX.Element | React.ReactFragment;
    width?: string | number | false;
    widthClass?: string;
    height?: string | number;
    mergeClasses?: string;
    bgColor?: string;
    disabled?: boolean;
    role?: string;
    tabIndex?: number;
    ariaLabel?: string;
    id?: string;
    style?: React.CSSProperties;
}

/******************************************** HELPERS *********************************************/
/**
 * Add given prefix if not present, leave if it is
 * If empty string given, return empty string
 *
 * @param {string} id Add prefix to start of this val if it doesn't start w prefix & isn't ``
 * @return {string} Given string with prefix at beginning (or `` if `` was given)
 */
const ensurePrefix = (id: string, prefix: string): string => {
    if (!id) return ``;
    return id.match(new RegExp(`^${prefix}\.[\s\S]*`)) ? id : `${prefix}${id}`;
};

/**
 * Convert [size] to a valid value to set a CSS size attribute (width, min-height, etc) to
 */
const cleanSizeForCss = (size: string | number | boolean | void) =>
    size && ((typeof size === `string` && size.endsWith(`px`)) || typeof size === `number`)
        ? size
        : `${size}px`;

/********************************************* EXPORT *********************************************/
// TODO make bgColor a less nonsensical prop for SubmitButton
/**
 * Submit button
 * Displays ladda as long as prop isLoading is true, which should be set up to
 * occur on click, then set to false again when the click-triggered action done
 * MANDATORY: onClick, labelId (as string ID) OR children (as string)
 *            To use ladda: isLoading
 */
export const SubmitButton = ({
    isLoading,
    onClick,
    labelId,
    width,
    widthClass,
    mergeClasses,
    bgColor,
    disabled,
    height,
    role,
    tabIndex,
    ariaLabel,
    id,
    style,
    children,
}: SubmitBtnProps) => {
    const title = children || translate(labelId || `submit`).capitalize;

    // Set up style object
    const cleanWidth = cleanSizeForCss(width);
    const cleanHeight = cleanSizeForCss(height);
    const styles = {
        ...(isStringOrNumber(width) && {
            width: cleanWidth,
            minWidth: cleanWidth,
        }),
        ...(isStringOrNumber(height) && {
            height: cleanHeight,
            minHeight: cleanHeight,
        }),
        ...style, // Note: NB to have this last, so it can clobber width & height set in other ways
    };

    // Set up parts of className
    const cleanWidthClass = isStringOrNumber(width) || widthClass || `ci-card-content-width`;
    const cleanHeightClass = isStringOrNumber(height) || s[`def-ladda-button-height`];

    const bgColorCSSClasses =
        (bgColor && ensurePrefix(bgColor, `bg-`)) ||
        `bg-blue bg-mid-dark-blue-hover bg-dark-blue-active`;

    return (
        <LaddaButton
            id={id || null}
            role={role || `button`}
            aria-label={ariaLabel || title}
            tabIndex={tabIndex || 0}
            // State
            loading={isLoading || false}
            disabled={disabled || false}
            // Actions / event handlers
            onKeyDown={(ev: React.SyntheticEvent<React.KeyboardEvent<any>>) => {
                if (ev.currentTarget.key === `Enter` || ev.currentTarget.key === `Space`) {
                    ev.preventDefault();
                    ev.stopPropagation();
                }
            }}
            onKeyPress={(ev: React.SyntheticEvent<React.KeyboardEvent<any>>) => {
                ev.preventDefault();
                ev.stopPropagation();
            }}
            onKeyUp={onSubmitKeyPress(onClick)}
            onClick={onClick}
            // Styling
            className={cn(
                s[`ci-submit-button`],
                cn(cleanWidthClass, cleanHeightClass),
                `helvetica-neue-light wk-smoothen-text`,
                bgColorCSSClasses,
                mergeClasses
            )}
            style={styles}
            // Module-specific
            data-color="#eee"
            data-size={XL}
            data-style={EXPAND_LEFT}
            data-spinner-size={30}
            data-spinner-color="#ddd"
            data-spinner-lines={12}
        >
            {title}
        </LaddaButton>
    );
};

SubmitButton.displayName = `SubmitButton`;
